"use client"
import { createTheme } from "@mui/material/styles"
import { GlobalStyle } from "./GlobalStyle"
import { colors } from "./colors"
import { mediaQueries } from "./mediaQueries"
import { sizes } from "./sizes"
import "./types"

export const sharedTheme = createTheme({
  direction: "rtl", // Set initial direction (will be changed dynamically)
  typography: {
    h1: {
      fontWeight: "700",
      color: colors.white,
      textAlign: "center",
      [`${mediaQueries.mobile}`]: {
        fontSize: "28px",
      },
      [`${mediaQueries.tablet}`]: {
        fontSize: "38px",
      },
      [`${mediaQueries.desktopXl}`]: {
        fontSize: "50px",
      },
    },
    h2: {
      fontWeight: "700",
      color: colors.white,
      textAlign: "center",
      [`${mediaQueries.mobile}`]: {
        fontSize: "28px",
      },
      [`${mediaQueries.tablet}`]: {
        fontSize: "38px",
      },
      [`${mediaQueries.desktopXl}`]: {
        fontSize: "50px",
      },
    },
    h3: {
      fontWeight: "400",
      color: colors.lightBlue,

      [`${mediaQueries.mobile}`]: {
        fontSize: "20px",
      },
      [`${mediaQueries.tablet}`]: {
        fontSize: "24px",
      },
      [`${mediaQueries.desktopXl}`]: {
        fontSize: "30px",
      },
    },
    h4: {
      fontWeight: "700",
      color: colors.black,

      [`${mediaQueries.mobile}`]: {
        fontSize: "18px",
      },

      [`${mediaQueries.tablet}`]: {
        fontSize: "18px",
      },

      [`${mediaQueries.desktopXl}`]: {
        fontSize: "22px",
      },
    },
    h5: {
      fontWeight: "700",
      color: colors.black,
      textAlign: "center",

      [`${mediaQueries.mobile}`]: {
        fontSize: "16px",
      },

      [`${mediaQueries.tablet}`]: {
        fontSize: "15px",
      },

      [`${mediaQueries.desktopXl}`]: {
        fontSize: "18px",
      },
    },
    body1: {
      fontSize: "14px",
      fontWeight: "400",
      color: colors.red,
      lineHeight: "22px",

      [`${mediaQueries.mobile}`]: {
        fontSize: "13px",
      },
    },
    body2: {
      fontSize: "16px",
      fontWeight: "400",
      lineHeight: "22px",

      [`${mediaQueries.mobile}`]: {
        fontSize: "13px",
      },
    },
    body3: {
      fontSize: "18px",
      fontWeight: "400",
      lineHeight: "27px",
      color: colors.black,
      textAlign: "start",

      [`${mediaQueries.mobile}`]: {
        fontSize: "13px",
      },
    },
    CTA: {
      fontSize: "15px",
      fontWeight: "700",
      color: colors.black,
      lineHeight: "18px",
      [`${mediaQueries.desktop}`]: {
        fontSize: "16px",
        lineHeight: "19.2px",
      },
    },
  },
  palette: {
    primary: {
      main: colors.black,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: GlobalStyle,
    },
    MuiFilledInput: {
      // Target the FilledInput component
      styleOverrides: {
        root: {
          boxSizing: "border-box",
          borderBottom: "none",
          outline: "1px solid #C2C1BF",
          borderWidth: 0,
          borderStyle: "solid",
          borderColor: "#C2C1BF", // Color of the border
          borderRadius: "28px", //
          paddingLeft: "20px",
          backgroundColor: "#FFF", // Set your desired background color
          "&:hover": {
            backgroundColor: "#FFF", // Optional: Background color on hover
            outline: "1px solid black",
          },
          "&.Mui-focused": {
            backgroundColor: "#FFF", // Optional: Background color when focused
          },
          "&.Mui-error": {
            border: "1px solid #C92418",
            outline: "none",
            "&:hover": {
              outline: "none",
            },
          },

          // Target the root element of the FilledInput component
          "&:after": {
            // Target the ':after' pseudo-element
            borderBottom: "none !important", // Apply the style override
          },
          "&:before": {
            borderBottom: "none !important",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          color: "black",
          width: "100%",
          "& .MuiInputLabel-formControl": {
            color: "var(--text-dark, #25272b)",
            textAlign: "right",
            fontFamily: "Brown Trial Heb",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "140%",
          },
          "& .MuiInputBase-input": {
            border: `1px solid ${colors.borderGray}`,
            borderRadius: "4px",
          },
          "& .MuiFormHelperText-root": {
            marginLeft: 0,
            fontSize: "12px",
          },
          [`${mediaQueries.desktop}`]: {
            "& .MuiInputLabel-formControl": {
              fontSize: "16px",
              fontWeight: 400,
            },
          },
          "& .MuiInputLabel-shrink": {
            display: "none",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          borderRadius: sizes.borderRadius,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          border: "none",
        },
      },
    },

    MuiInputLabel: {
      // Target the InputLabel component
      styleOverrides: {
        root: {
          // Define styles for different states
          fontSize: "22px",
          color: "#535352", // Default label color
          top: "3px",
          fontWeight: "300",
          transform: "translate(12px, 12px) scale(1)",
          paddingLeft: "8px",
          "&.Mui-focused": {
            color: "#6F6F6F", // Color when the input is focused
          },

          "&.Mui-error": {
            color: "#6F6F6F", // Color when there's an error
          },
          "&.MuiInputLabel-shrink": {
            transform: "translate(12px, 3px) scale(0.75) !important",
          },
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: "outlined" },
          style: {
            border: `1px solid ${colors.red}`,
            color: colors.black,
            background: colors.white,
            boxShadow: "none",
            "&:hover, &:focus, &:focus-visible": {
              background: colors.beige,
              "&.orange": {
                backgroundColor: colors.white,
                color: colors.black,
                border: `1.5px solid ${colors.orange}`,
              },
              "&.turquoise": {
                backgroundColor: colors.white,
                color: colors.black,
                border: `1.5px solid ${colors.turquoise}`,
              },
              "&.blue, &.darkBlue": {
                backgroundColor: colors.white,
                color: colors.black,
                border: `1.5px solid ${colors.blue}`,
              },
              "&.red, &.darkOrange": {
                backgroundColor: colors.white,
                color: colors.black,
                border: `1.5px solid ${colors.red}`,
              },
            },
          },
        },
        {
          props: { variant: "ghost" },
          style: {
            border: "none",
            color: colors.black,
            backgroundColor: "transparent",
            boxShadow: "none",
            "&:hover, &:focus, &:focus-visible, &.active": {
              backgroundColor: "transparent",
              color: colors.red,
            },
          },
        },
        {
          props: { variant: "elevated" },
          style: {
            color: colors.black,
            background: colors.white,
            boxShadow: colors.dropShadow.main,
            border: `1.5px solid transparent`,
            "&:hover, &:focus, &:focus-visible, &.active": {
              backgroundColor: colors.white,
              color: colors.black,
              border: `1.5px solid ${colors.orange}`,
              "&.orange": {
                // backgroundColor: colors.lightOrange,
                backgroundColor: colors.white,
                color: colors.black,
                border: `1.5px solid ${colors.orange}`,
              },
              "&.turquoise": {
                // backgroundColor: colors.lightTurquoise,
                backgroundColor: colors.white,
                color: colors.black,
                border: `1.5px solid ${colors.turquoise}`,
              },
              "&.blue, &.darkBlue": {
                // backgroundColor: colors.bluishGray,
                backgroundColor: colors.white,
                color: colors.black,
                border: `1.5px solid ${colors.blue}`,
              },
              "&.red, &.darkOrange": {
                // backgroundColor: colors.lightRed,
                backgroundColor: colors.white,
                color: colors.black,
                border: `1.5px solid ${colors.red}`,
              },
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          width: "fit-content",
          padding: `10px 18px`,
          borderRadius: sizes.radius.circle,
          background: colors.red,
          color: colors.white,
          fontSize: sizes.fonts.button,
          lineHeight: "19px",
          fontWeight: 700,
          boxShadow: colors.dropShadow.mainSmall,
          [`${mediaQueries.desktopM}`]: {
            fontSize: sizes.fonts.desktop.button,
            padding: `${sizes.baseDouble} ${sizes.baseTriple}`,
          },
          span: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          },
          "&.small": {},
          "&:hover, &:focus, &:focus-visible, &.active": {
            background: colors.darkBlue,
          },

          "&.Mui-disabled": {
            background: colors.lightGray,
            color: colors.gray,
          },
          svg: {
            pointerEvents: "none",
            marginInlineStart: ".4em",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          // Hide the default bottom border
          display: "none",
        },
      },
    },

    MuiTab: {},
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: "56px",
          alignItems: "flex-end",
          "&.MuiInput-root::before": {
            borderBottom: `1px solid ${colors.darkBlue}`,
            opacity: 0.3,
          },
          "&.Mui-error::before": {
            borderBottom: `1px solid ${colors.red}`,
            opacity: 1,
          },

          "&.MuiInput-root::after": {
            borderBottom: `1px solid ${colors.blue}`,
          },

          "&.Mui-error::after": {
            borderBottom: `1px solid ${colors.red}`,
            opacity: 1,
          },
        },
        input: {
          fontSize: "18px",
          lineHeight: "24px",
          paddingLeft: "0px",
          "&:focus::placeholder": {
            fontWeight: "500",
          },
          "&::placeholder": {
            fontSize: "18px",
            lineHeight: "24px",
            color: colors.black,
            fontWeight: "400",
            opacity: 1,
          },
        },
        inputMultiline: {},
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: "#BF1818",
          lineHeight: "16px",
          fontWeight: 300,
          margin: 0,
          fontSize: "16px",
          left: "10px",
          paddingLeft: "20px",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: "0px",
          width: "auto",
        },
        list: {
          justifyContent: "space-between",
          display: "flex",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "&:hover": {},
          padding: 0,
        },
        checked: {},
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          [`${mediaQueries.mobile}`]: {
            width: "auto",
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
            padding: "0 20px 32px",
          },
          [`${mediaQueries.tablet}`]: {
            width: "auto",
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {},
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
          margin: 0,
          listStyle: "none",
          gap: " 0 20px",
          [`${mediaQueries.desktopM}`]: {
            "& li:hover p": {
              color: "red",
            },
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          [`${mediaQueries.mobile}`]: {
            padding: 0,
          },
          [`${mediaQueries.tablet}`]: {
            padding: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: {
          [`${mediaQueries.mobile}`]: {
            marginTop: "16px",
            marginBottom: "16px",
          },
          [`${mediaQueries.tablet}`]: {
            marginTop: "16px",
            marginBottom: "16px",
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      mobile: 0,
      tablet: 1000,
      smLaptop: 1279,
      laptop: 1330,
      lgLaptop: 1512,
      desktop: 1920,
    },
  },
})

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: false // removes the `xs` breakpoint
    sm: false
    md: false
    lg: false
    xl: false
    mobile: true // adds the `mobile` breakpoint
    tablet: true
    smLaptop: true
    laptop: true
    lgLaptop: true
    desktop: true
  }
}

const customTheme = createTheme({
  ...sharedTheme,
})

export const theme: typeof customTheme & {
  colors: typeof colors
  sizes: typeof sizes
  mediaQueries: typeof mediaQueries
} = {
  ...customTheme,
  colors,
  sizes,
  mediaQueries,
}

export type Theme = typeof theme
export { colors, mediaQueries, sizes }
